import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Input,
  Button,
  Grid,
  Snackbar,
  CssBaseline,
  IconButton,
  Tooltip,
  Breadcrumbs,
  Modal,
  ModalDialog,
  ModalClose,
  FormControl,
  FormHelperText,
  FormLabel,
  Stack,
  Select,
  Option,
  Textarea,
  Card,
  CardContent,
  Divider,
  Alert,
} from '@mui/joy';
import {
  Search as SearchIcon,
  KeyboardArrowRight,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Visibility as VisibilityIcon,
  CurrencyExchange as CurrencyExchangeIcon,
  PriceChange as PriceChangeIcon,
  LocalOffer as LocalOfferIcon,
} from '@mui/icons-material';
import Link from '@mui/joy/Link';
import { DataGrid } from '@mui/x-data-grid';
import NativeCheckbox from '../../main/components/NativeCheckbox';
import {
  listShipmentUnits,
  getShipmentUnit,
  createShipmentUnit,
  updateShipmentUnit,
  deleteShipmentUnit,
  recalculateShipmentUnitPrices,
  bulkUpdateShipmentUnitTariffCode,
  listShipmentUnitPricingRules,
} from '../../main/API/shipmentUnit';
import { listPricingRules } from '../../main/API/pricingRule';
import UnitPricingRulesPanel from './widgets/UnitPricingRulesPanel';
import { getShippingCompany } from '../../main/API/shippingCompany';
import { getShippingWarehouse } from '../../main/API/shippingWarehouse';
import { getLocation } from '../../main/API/location';
import { listTrackingEventTypes } from '../../main/API/trackingEventType';
import useTrackingEventTypeOptions from '../../main/hooks/useTrackingEventTypeOptions';
import { labelForEventTypeCode, eventTypeDisplayFromRow } from '../../main/utils/trackingEventLabels';
import { getLocationDisplayName, getLocationDisplayNameFromRow, locationOptionFromApi, toLocationWritePk } from '../../main/utils/locationDisplayName';
import {
  nestedMasterOptionFromApi,
  nestedMasterOptionHasName,
  toNestedMasterFkPk,
} from '../../main/utils/nestedMasterFk';
import ShippingCompanyOptionList from '../../main/widgets/ShippingCompanyOptionList';
import ShippingWarehouseOptionList from '../../main/widgets/ShippingWarehouseOptionList';
import LocationOptionList from '../../main/widgets/LocationOptionList';
import TrackingEventTypeOptionList from '../../main/widgets/TrackingEventTypeOptionList';
import StatusOptionList from '../../main/widgets/StatusOptionList';
import PermissionGate from '../../main/components/PermissionGate';
import { PERMISSIONS } from '../../main/context/AuthContext';
import { usePermissions } from '../../main/hooks/usePermissions';
import { DirectionalMaterialThemeProvider } from '../../main/theme/DirectionalMaterialThemeProvider';
import { masterApiErrorMessage } from '../../main/utils/masterApiError';
import { fetchAllPages, getPaginatedMeta, getPaginatedResults } from '../../main/utils/pagination';
import TrackingEventControlFields from '../../main/widgets/TrackingEventControlFields';
import {
  applyTrackingEventControl,
  defaultTrackingEventFormFields,
  trackingDecisionFromFormFields,
  validateTrackingEventFormFields,
} from '../../main/utils/trackingEventControl';

function parseShipmentIds(text) {
  if (!text || !String(text).trim()) return [];
  return String(text)
    .split(/[,;\s]+/)
    .map((s) => parseInt(s.trim(), 10))
    .filter((n) => !Number.isNaN(n));
}

/** Nested warehouse/company object or legacy bare id → display label. */
function masterFkDisplayLabel(value, language, dash) {
  if (value == null || value === '') return dash;
  if (typeof value === 'object') {
    const name = getLocationDisplayName(value, language);
    return name || (value.id != null ? String(value.id) : dash);
  }
  return String(value);
}

/** API ISO instant → `YYYY-MM-DDTHH:mm` for one datetime-local field (local timezone). */
function isoToDatetimeLocalValue(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const hh = String(d.getHours()).padStart(2, '0');
  const min = String(d.getMinutes()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}T${hh}:${min}`;
}

/** When both expected arrival dates are set, destination must be strictly before warehouse. */
function validateDestinationBeforeWarehouseEta(form, t) {
  const dest = form.expected_destination_eta && String(form.expected_destination_eta).trim();
  const wh = form.expected_warehouse_eta && String(form.expected_warehouse_eta).trim();
  if (!dest || !wh) return null;
  const dDest = new Date(`${dest}T00:00:00`);
  const dWh = new Date(`${wh}T00:00:00`);
  if (Number.isNaN(dDest.getTime()) || Number.isNaN(dWh.getTime())) return null;
  if (dDest.getTime() >= dWh.getTime()) {
    return t('shipmentUnit.valEtaOrder');
  }
  return null;
}

const UNIT_NUMBER_MAX_LENGTH = 128;
const PRICING_TARIFF_CODE_MAX_LENGTH = 64;

function emptyFieldErrors() {
  return {
    serial_number: '',
    unit_number: '',
    shipments_ids_text: '',
    shipped_at: '',
    shipping_company: '',
    origin_warehouse: '',
    destination_warehouse: '',
    location: '',
    tracking_url: '',
    expected_destination_eta: '',
    expected_warehouse_eta: '',
    eta_order: '',
    ship_name: '',
    status: '',
    event_type: '',
    note: '',
    tracking_event_timestamp: '',
  };
}

function fieldMessage(v) {
  if (v == null) return '';
  if (Array.isArray(v)) return v.map(String).join(' ');
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

function mergeApiFieldErrors(data) {
  const fieldErrors = emptyFieldErrors();
  let submitError = '';
  if (!data || typeof data !== 'object') {
    return { fieldErrors, submitError };
  }
  const map = {
    serial_number: 'serial_number',
    unit_number: 'unit_number',
    shipments: 'shipments_ids_text',
    shipped_at: 'shipped_at',
    shipping_company: 'shipping_company',
    origin_warehouse: 'origin_warehouse',
    destination_warehouse: 'destination_warehouse',
    location: 'location',
    location_id: 'location',
    tracking_url: 'tracking_url',
    expected_destination_eta: 'expected_destination_eta',
    expected_warehouse_eta: 'expected_warehouse_eta',
    ship_name: 'ship_name',
    status: 'status',
    event_type: 'event_type',
    note: 'note',
    tracking_event_timestamp: 'tracking_event_timestamp',
  };
  Object.keys(map).forEach((apiKey) => {
    if (data[apiKey] != null) {
      fieldErrors[map[apiKey]] = fieldMessage(data[apiKey]);
    }
  });
  if (typeof data.detail === 'string' && data.detail.trim()) {
    submitError = data.detail.trim();
  } else if (Array.isArray(data.non_field_errors) && data.non_field_errors.length) {
    submitError = data.non_field_errors.map(String).join(' ');
  }
  return { fieldErrors, submitError };
}

function validateClientShipmentUnitForm(form, t) {
  const e = emptyFieldErrors();
  if (!form.serial_number || !String(form.serial_number).trim()) {
    e.serial_number = t('shipmentUnit.valSerialRequired');
  }
  const unitNum = form.unit_number != null ? String(form.unit_number) : '';
  if (unitNum.length > UNIT_NUMBER_MAX_LENGTH) {
    e.unit_number = t('shipmentUnit.valUnitNumberMax', { max: UNIT_NUMBER_MAX_LENGTH });
  }
  const etaMsg = validateDestinationBeforeWarehouseEta(form, t);
  if (etaMsg) {
    e.eta_order = etaMsg;
  }
  if (!form.status || !String(form.status).trim()) {
    e.status = t('shipmentUnit.valStatusRequired');
  }
  if (!form.location || !String(form.location).trim()) {
    e.location = t('shipmentUnit.valLocationRequired');
  }
  const trackingTsErr = validateTrackingEventFormFields(
    form.tracking_event_mode,
    form.tracking_event_timestamp,
    t,
  );
  if (trackingTsErr) {
    e.tracking_event_timestamp = trackingTsErr;
  }
  return e;
}

function hasAnyFieldError(errors) {
  return Object.keys(errors).some((k) => errors[k] && String(errors[k]).trim());
}

function shipmentCountFromUnit(unit) {
  if (!unit || !Array.isArray(unit.shipments)) return null;
  return unit.shipments.length;
}

function scrollToFirstFieldError(errors, anchorsRef) {
  if (!errors || !anchorsRef) return;
  const refs = anchorsRef.current;
  if (!refs) return;
  const tryScroll = (key) => {
    const el = refs[key];
    if (el && typeof el.scrollIntoView === 'function') {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return true;
    }
    return false;
  };

  if (errors.serial_number && tryScroll('serial_number')) return;
  if (errors.unit_number && tryScroll('unit_number')) return;
  if (errors.shipments_ids_text && tryScroll('shipments_ids_text')) return;
  if (errors.shipped_at && tryScroll('shipped_at')) return;
  if (errors.shipping_company && tryScroll('shipping_company')) return;
  if (errors.origin_warehouse && tryScroll('origin_warehouse')) return;
  if (errors.destination_warehouse && tryScroll('destination_warehouse')) return;
  if (errors.location && tryScroll('location')) return;
  if (errors.tracking_url && tryScroll('tracking_url')) return;
  if (errors.expected_destination_eta || errors.expected_warehouse_eta || errors.eta_order) {
    if (tryScroll('expected_dates')) return;
  }
  if (errors.ship_name && tryScroll('ship_name')) return;
  if (errors.status && tryScroll('status')) return;
  if (errors.event_type && tryScroll('event_type')) return;
  if (errors.tracking_event_timestamp && tryScroll('tracking_event_timestamp')) return;
  if (errors.note && tryScroll('note')) return;
}

function scrollToSubmitBanner(bannerRef) {
  if (bannerRef && bannerRef.current && typeof bannerRef.current.scrollIntoView === 'function') {
    bannerRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
}

const emptyForm = {
  serial_number: '',
  unit_number: '',
  shipments_ids_text: '',
  shipped_at: '',
  shipping_company: '',
  origin_warehouse: '',
  destination_warehouse: '',
  location: '',
  tracking_url: '',
  expected_destination_eta: '',
  expected_warehouse_eta: '',
  ship_name: '',
  note: '',
  status: '',
  event_type: '',
  /** PATCH/PUT only; ignored on create. */
  notify_owners_whatsapp: false,
  ...defaultTrackingEventFormFields(),
};

export default function ShipmentUnitManagerScreen() {
  const { t, i18n } = useTranslation('ui');
  const location = useLocation();
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const canListEventTypes = hasPermission(PERMISSIONS.SHIPMENT_TRACKING_EVENT_TYPES_LIST);
  const { options: eventTypeOptions } = useTrackingEventTypeOptions(canListEventTypes);
  const [loading, setLoading] = useState(false);
  const [units, setUnits] = useState([]);
  const [data, setData] = useState(null);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 50 });
  const [filters, setFilters] = useState({
    search: '',
    deleted: '',
  });
  const [filterShippingCompanyOpt, setFilterShippingCompanyOpt] = useState(null);
  const [filterOriginWarehouseOpt, setFilterOriginWarehouseOpt] = useState(null);
  const [filterDestinationWarehouseOpt, setFilterDestinationWarehouseOpt] = useState(null);
  const [filterLocationOpt, setFilterLocationOpt] = useState(null);

  const [createCompanyOpt, setCreateCompanyOpt] = useState(null);
  const [createWarehouseOpt, setCreateWarehouseOpt] = useState(null);
  const [createDestWarehouseOpt, setCreateDestWarehouseOpt] = useState(null);
  const [createLocationOpt, setCreateLocationOpt] = useState(null);
  const [createEventTypeOpt, setCreateEventTypeOpt] = useState(null);

  const [editCompanyOpt, setEditCompanyOpt] = useState(null);
  const [editWarehouseOpt, setEditWarehouseOpt] = useState(null);
  const [editDestWarehouseOpt, setEditDestWarehouseOpt] = useState(null);
  const [editLocationOpt, setEditLocationOpt] = useState(null);
  const [editEventTypeOpt, setEditEventTypeOpt] = useState(null);

  const [failedNotification, setFailedNotification] = useState(false);
  const [successNotification, setSuccessNotification] = useState(false);
  const [responseMessage, setResponseMessage] = useState('');

  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [createForm, setCreateForm] = useState(emptyForm);

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState(emptyForm);

  const [createFieldErrors, setCreateFieldErrors] = useState(() => emptyFieldErrors());
  const [editFieldErrors, setEditFieldErrors] = useState(() => emptyFieldErrors());
  const [createSubmitError, setCreateSubmitError] = useState('');
  const [editSubmitError, setEditSubmitError] = useState('');

  const createErrorAnchorsRef = useRef({});
  const editErrorAnchorsRef = useRef({});
  const createSubmitBannerRef = useRef(null);
  const editSubmitBannerRef = useRef(null);

  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [detailUnit, setDetailUnit] = useState(null);

  const [recalcConfirmOpen, setRecalcConfirmOpen] = useState(false);
  const [recalcLoading, setRecalcLoading] = useState(false);
  const [recalcTargetUnit, setRecalcTargetUnit] = useState(null);

  const [bulkTariffModalOpen, setBulkTariffModalOpen] = useState(false);
  const [bulkTariffTargetUnit, setBulkTariffTargetUnit] = useState(null);
  const [bulkTariffCode, setBulkTariffCode] = useState('');
  const [bulkTariffRecalculate, setBulkTariffRecalculate] = useState(true);
  const [bulkTariffLoading, setBulkTariffLoading] = useState(false);
  const [bulkTariffFieldError, setBulkTariffFieldError] = useState('');
  const [bulkTariffSubmitError, setBulkTariffSubmitError] = useState('');
  const [bulkTariffOptions, setBulkTariffOptions] = useState([]);

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deletingUnit, setDeletingUnit] = useState(null);

  useEffect(() => {
    const state = location.state;
    if (!state?.pricingRuleJustCreated) return;
    setResponseMessage(t('pricingRules.snackbarCreated'));
    setSuccessNotification(true);
    if (state.promptUnitRecalc) {
      const unitId = state.promptUnitRecalc;
      getShipmentUnit(unitId)
        .then((res) => {
          setDetailUnit(res.data);
          setViewModalOpen(true);
        })
        .catch(() => {});
    }
    navigate(
      { pathname: location.pathname, search: location.search, hash: location.hash },
      { replace: true, state: {} }
    );
  }, [
    location.state,
    location.pathname,
    location.search,
    location.hash,
    navigate,
    t,
  ]);

  const loadUnits = useCallback(async () => {
    if (!hasPermission(PERMISSIONS.SHIPMENT_UNITS_LIST)) {
      setUnits([]);
      return;
    }
    setLoading(true);
    try {
      const params = {
        page: paginationModel.page + 1,
        page_size: paginationModel.pageSize,
        pagination: true,
      };
      if (filters.search) params.search = filters.search;
      if (filterShippingCompanyOpt && filterShippingCompanyOpt.id != null) {
        params.shipping_company = filterShippingCompanyOpt.id;
      }
      if (filterOriginWarehouseOpt && filterOriginWarehouseOpt.id != null) {
        params.origin_warehouse = filterOriginWarehouseOpt.id;
      }
      if (filterDestinationWarehouseOpt && filterDestinationWarehouseOpt.id != null) {
        params.destination_warehouse = filterDestinationWarehouseOpt.id;
      }
      if (filterLocationOpt && filterLocationOpt.id != null) {
        params.location = filterLocationOpt.id;
      }
      if (filters.deleted === 'true') params.deleted = true;
      if (filters.deleted === 'false') params.deleted = false;

      const response = await listShipmentUnits(params);
      const meta = getPaginatedMeta(response.data, paginationModel.pageSize);
      setData(meta);
      setUnits(meta.results);
    } catch (error) {
      console.error('Failed to load shipment units:', error);
      setResponseMessage(masterApiErrorMessage(error, t) || t('shipmentUnit.loadFailed'));
      setFailedNotification(true);
      setUnits([]);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [
    hasPermission,
    paginationModel.page,
    paginationModel.pageSize,
    filters,
    filterShippingCompanyOpt,
    filterOriginWarehouseOpt,
    filterDestinationWarehouseOpt,
    filterLocationOpt,
    t,
  ]);

  // Filters reload via Apply; this effect only refetches when pagination changes.
  useEffect(() => {
    loadUnits();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paginationModel.page, paginationModel.pageSize]);

  const handleSubmitFilters = (e) => {
    e.preventDefault();
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
    loadUnits();
  };

  /**
   * Payload for create/update. On update, the server applies changes to **current location**,
   * **status**, and **event type** to every shipment in the unit and creates a **shipment
   * tracking event** for each of those shipments (see edit modal note).
   */
  const buildPayload = (form, { isEdit = false } = {}) => {
    const out = {
      serial_number: form.serial_number.trim(),
      unit_number: form.unit_number != null ? String(form.unit_number).trim() : '',
      shipments: parseShipmentIds(form.shipments_ids_text),
      tracking_url: form.tracking_url || '',
      ship_name: form.ship_name || '',
      note: form.note || '',
      status: form.status || '',
    };
    if (form.shipped_at && String(form.shipped_at).trim()) {
      const local = new Date(form.shipped_at);
      if (!Number.isNaN(local.getTime())) {
        out.shipped_at = local.toISOString();
      }
    }
    // GET returns nested objects; writes still require integer PKs.
    out.shipping_company = toNestedMasterFkPk(form.shipping_company);
    out.origin_warehouse = toNestedMasterFkPk(form.origin_warehouse);
    out.destination_warehouse = toNestedMasterFkPk(form.destination_warehouse);
    const loc = form.location;
    out.location = toLocationWritePk(loc !== '' && loc != null ? loc : null);
    out.expected_destination_eta = form.expected_destination_eta || null;
    out.expected_warehouse_eta = form.expected_warehouse_eta || null;
    const et = form.event_type && String(form.event_type).trim();
    if (et) {
      out.event_type = et;
    } else if (isEdit) {
      out.event_type = null;
    }
    if (isEdit) {
      out.notify_owners_whatsapp = !!form.notify_owners_whatsapp;
    }
    return out;
  };

  const handleCreate = async () => {
    if (!hasPermission(PERMISSIONS.SHIPMENT_UNITS_CREATE)) return;
    setCreateSubmitError('');
    const clientErrs = validateClientShipmentUnitForm(createForm, t);
    if (hasAnyFieldError(clientErrs)) {
      setCreateFieldErrors(clientErrs);
      setTimeout(() => scrollToFirstFieldError(clientErrs, createErrorAnchorsRef), 50);
      return;
    }
    setCreateFieldErrors(emptyFieldErrors());
    setLoading(true);
    try {
      const trackingDecision = trackingDecisionFromFormFields(
        createForm.tracking_event_mode,
        createForm.tracking_event_timestamp,
      );
      const payload = applyTrackingEventControl(
        buildPayload(createForm, { isEdit: false }),
        trackingDecision,
      );
      await createShipmentUnit(payload);
      setResponseMessage(t('shipmentUnit.createdSuccess'));
      setSuccessNotification(true);
      setCreateModalOpen(false);
      setCreateForm(emptyForm);
      setCreateFieldErrors(emptyFieldErrors());
      setCreateSubmitError('');
      loadUnits();
    } catch (error) {
      const res = error.response;
      if (res && res.status === 400 && res.data && typeof res.data === 'object' && !Array.isArray(res.data)) {
        const { fieldErrors, submitError } = mergeApiFieldErrors(res.data);
        const hasFields = hasAnyFieldError(fieldErrors);
        setCreateFieldErrors(fieldErrors);
        if (submitError) {
          setCreateSubmitError(submitError);
        } else if (!hasFields) {
          setCreateSubmitError(masterApiErrorMessage(error, t) || t('shipmentUnit.createFailed'));
        } else {
          setCreateSubmitError('');
        }
        setTimeout(() => {
          if (hasFields) {
            scrollToFirstFieldError(fieldErrors, createErrorAnchorsRef);
          } else {
            scrollToSubmitBanner(createSubmitBannerRef);
          }
        }, 50);
      } else {
        setCreateFieldErrors(emptyFieldErrors());
        setCreateSubmitError(masterApiErrorMessage(error, t) || t('shipmentUnit.createFailed'));
        setTimeout(() => scrollToSubmitBanner(createSubmitBannerRef), 50);
      }
    } finally {
      setLoading(false);
    }
  };

  const openEdit = async (row) => {
    if (!hasPermission(PERMISSIONS.SHIPMENT_UNITS_VIEW)) return;
    setLoading(true);
    try {
      const res = await getShipmentUnit(row.id);
      const u = res.data;
      const ids = Array.isArray(u.shipments)
        ? u.shipments.map((s) => (typeof s === 'object' && s != null ? s.id : s)).filter((id) => id != null).join(', ')
        : '';

      // Prefer nested GET objects; only fetch when we only have a bare id.
      let companyOpt = nestedMasterOptionFromApi(u.shipping_company);
      if (companyOpt?.id && !nestedMasterOptionHasName(companyOpt)) {
        try {
          const cr = await getShippingCompany(companyOpt.id);
          companyOpt = cr.data;
        } catch (_) {
          companyOpt = {
            id: companyOpt.id,
            name: t('shipmentUnit.companyFallback', { id: companyOpt.id }),
            code: '',
          };
        }
      }
      let warehouseOpt = nestedMasterOptionFromApi(u.origin_warehouse);
      if (warehouseOpt?.id && !nestedMasterOptionHasName(warehouseOpt)) {
        try {
          const wr = await getShippingWarehouse(warehouseOpt.id);
          warehouseOpt = wr.data;
        } catch (_) {
          warehouseOpt = {
            id: warehouseOpt.id,
            name: t('shipmentUnit.warehouseFallback', { id: warehouseOpt.id }),
            code: '',
          };
        }
      }
      let destWarehouseOpt = nestedMasterOptionFromApi(u.destination_warehouse);
      if (destWarehouseOpt?.id && !nestedMasterOptionHasName(destWarehouseOpt)) {
        try {
          const dwr = await getShippingWarehouse(destWarehouseOpt.id);
          destWarehouseOpt = dwr.data;
        } catch (_) {
          destWarehouseOpt = {
            id: destWarehouseOpt.id,
            name: t('shipmentUnit.warehouseFallback', { id: destWarehouseOpt.id }),
            code: '',
          };
        }
      }
      let locationOpt = locationOptionFromApi(u);
      if (locationOpt?.id) {
        const hasName = Boolean(
          (locationOpt.name && String(locationOpt.name).trim())
          || (locationOpt.name_ar && String(locationOpt.name_ar).trim())
        );
        if (!hasName) {
          try {
            const lr = await getLocation(locationOpt.id);
            locationOpt = lr.data;
          } catch (_) {
            locationOpt = {
              id: locationOpt.id,
              name: typeof u.location === 'string' ? u.location : t('shipmentUnit.locationFallback', { id: locationOpt.id }),
            };
          }
        }
      } else {
        locationOpt = null;
      }
      let eventTypeOpt = null;
      if (u.event_type) {
        try {
          const tr = await listTrackingEventTypes({
            code: u.event_type,
            page_size: 1,
            pagination: true,
          });
          const tData = tr.data;
          const tRows =
            tData && tData.results !== undefined
              ? tData.results
              : Array.isArray(tData)
                ? tData
                : [];
          eventTypeOpt = tRows[0] || { code: u.event_type, name: u.event_type };
        } catch (_) {
          eventTypeOpt = { code: u.event_type, name: u.event_type };
        }
      }

      setEditingId(u.id);
      setEditCompanyOpt(companyOpt);
      setEditWarehouseOpt(warehouseOpt);
      setEditDestWarehouseOpt(destWarehouseOpt);
      setEditLocationOpt(locationOpt);
      setEditEventTypeOpt(eventTypeOpt);
      setEditForm({
        serial_number: u.serial_number || '',
        unit_number: u.unit_number != null ? String(u.unit_number) : '',
        shipments_ids_text: ids,
        shipped_at: isoToDatetimeLocalValue(u.shipped_at),
        shipping_company: companyOpt?.id != null ? String(companyOpt.id) : '',
        origin_warehouse: warehouseOpt?.id != null ? String(warehouseOpt.id) : '',
        destination_warehouse: destWarehouseOpt?.id != null ? String(destWarehouseOpt.id) : '',
        location: locationOpt?.id != null ? String(locationOpt.id) : '',
        tracking_url: u.tracking_url || '',
        expected_destination_eta: u.expected_destination_eta || '',
        expected_warehouse_eta: u.expected_warehouse_eta || '',
        ship_name: u.ship_name || '',
        note: u.note || '',
        status: u.status || '',
        event_type: u.event_type || '',
        notify_owners_whatsapp: false,
        ...defaultTrackingEventFormFields(),
      });
      setEditFieldErrors(emptyFieldErrors());
      setEditSubmitError('');
      setEditModalOpen(true);
    } catch (error) {
      setResponseMessage(masterApiErrorMessage(error, t) || t('shipmentUnit.loadUnitFailed'));
      setFailedNotification(true);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Saving the unit triggers server-side sync: location / status / event type updates roll out to
   * all member shipments and add a tracking event per shipment.
   */
  const handleUpdate = async () => {
    if (!editingId) return;
    if (!hasPermission(PERMISSIONS.SHIPMENT_UNITS_UPDATE)) return;
    setEditSubmitError('');
    const clientErrs = validateClientShipmentUnitForm(editForm, t);
    if (hasAnyFieldError(clientErrs)) {
      setEditFieldErrors(clientErrs);
      setTimeout(() => scrollToFirstFieldError(clientErrs, editErrorAnchorsRef), 50);
      return;
    }
    setEditFieldErrors(emptyFieldErrors());
    setLoading(true);
    try {
      const trackingDecision = trackingDecisionFromFormFields(
        editForm.tracking_event_mode,
        editForm.tracking_event_timestamp,
      );
      const payload = applyTrackingEventControl(
        buildPayload(editForm, { isEdit: true }),
        trackingDecision,
      );
      const updateRes = await updateShipmentUnit(
        editingId,
        payload,
        true,
      );
      const data = updateRes && updateRes.data;
      let msg = t('shipmentUnit.updatedSuccess');
      if (data && data.notify_owners_whatsapp_result) {
        const r = data.notify_owners_whatsapp_result;
        if (r.skipped) {
          const reason =
            r.reason === 'missing_permission'
              ? t('shipmentUnit.whatsappReasonMissingPermission')
              : r.reason || 'skipped';
          msg += ` ${t('shipmentUnit.whatsappNotSent', { reason })}`;
        } else {
          const sent = r.sent != null ? r.sent : 0;
          const failed = r.failed != null ? r.failed : 0;
          const owners = r.owners_reached != null ? r.owners_reached : 0;
          msg += ` ${
            owners === 1
              ? t('shipmentUnit.whatsappSummaryOneOwner', { sent, failed })
              : t('shipmentUnit.whatsappSummary', { sent, failed, owners })
          }`;
        }
      }
      setResponseMessage(msg);
      setSuccessNotification(true);
      setEditModalOpen(false);
      setEditingId(null);
      setEditFieldErrors(emptyFieldErrors());
      setEditSubmitError('');
      loadUnits();
    } catch (error) {
      const res = error.response;
      if (res && res.status === 400 && res.data && typeof res.data === 'object' && !Array.isArray(res.data)) {
        const { fieldErrors, submitError } = mergeApiFieldErrors(res.data);
        const hasFields = hasAnyFieldError(fieldErrors);
        setEditFieldErrors(fieldErrors);
        if (submitError) {
          setEditSubmitError(submitError);
        } else if (!hasFields) {
          setEditSubmitError(masterApiErrorMessage(error, t) || t('shipmentUnit.updateFailed'));
        } else {
          setEditSubmitError('');
        }
        setTimeout(() => {
          if (hasFields) {
            scrollToFirstFieldError(fieldErrors, editErrorAnchorsRef);
          } else {
            scrollToSubmitBanner(editSubmitBannerRef);
          }
        }, 50);
      } else {
        setEditFieldErrors(emptyFieldErrors());
        setEditSubmitError(masterApiErrorMessage(error, t) || t('shipmentUnit.updateFailed'));
        setTimeout(() => scrollToSubmitBanner(editSubmitBannerRef), 50);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deletingUnit) return;
    if (!hasPermission(PERMISSIONS.SHIPMENT_UNITS_DELETE)) return;
    setLoading(true);
    try {
      await deleteShipmentUnit(deletingUnit.id);
      setResponseMessage(t('shipmentUnit.deletedSuccess'));
      setSuccessNotification(true);
      setDeleteModalOpen(false);
      setDeletingUnit(null);
      loadUnits();
    } catch (error) {
      setResponseMessage(masterApiErrorMessage(error, t) || t('shipmentUnit.deleteFailed'));
      setFailedNotification(true);
    } finally {
      setLoading(false);
    }
  };

  const openView = async (row) => {
    setLoading(true);
    try {
      const res = await getShipmentUnit(row.id);
      setDetailUnit(res.data);
      setViewModalOpen(true);
    } catch (error) {
      setResponseMessage(masterApiErrorMessage(error, t) || t('shipmentUnit.loadUnitFailed'));
      setFailedNotification(true);
    } finally {
      setLoading(false);
    }
  };

  const recalcShipmentCount = shipmentCountFromUnit(recalcTargetUnit);
  const bulkTariffShipmentCount = shipmentCountFromUnit(bulkTariffTargetUnit);

  const loadTariffCodeOptions = useCallback(async (unitId) => {
    if (!unitId || !hasPermission(PERMISSIONS.PRICING_RULES_LIST)) {
      setBulkTariffOptions([]);
      return;
    }
    const codes = new Set();
    try {
      const [unitRes, globalRules] = await Promise.all([
        listShipmentUnitPricingRules(unitId).catch(() => ({ data: { results: [] } })),
        fetchAllPages(
          (page, pageSize) => listPricingRules({ active: true, page, page_size: pageSize }),
        ).catch(() => []),
      ]);
      const unitRules = getPaginatedResults(unitRes?.data);
      [...unitRules, ...globalRules].forEach((rule) => {
        const tc = rule?.tariff_code != null ? String(rule.tariff_code).trim() : '';
        if (tc) codes.add(tc);
      });
      setBulkTariffOptions([...codes].sort((a, b) => a.localeCompare(b)));
    } catch (_) {
      setBulkTariffOptions([]);
    }
  }, [hasPermission]);

  const openBulkTariffModal = async (unit) => {
    if (!unit?.id || !hasPermission(PERMISSIONS.SHIPMENTS_UPDATE)) return;
    let targetUnit = unit;
    if (!Array.isArray(unit.shipments)) {
      try {
        const res = await getShipmentUnit(unit.id);
        targetUnit = res.data;
      } catch (_) {
        // keep list row for count-unknown confirm copy
      }
    }
    setBulkTariffTargetUnit(targetUnit);
    setBulkTariffCode('');
    setBulkTariffRecalculate(true);
    setBulkTariffFieldError('');
    setBulkTariffSubmitError('');
    setBulkTariffModalOpen(true);
    await loadTariffCodeOptions(unit.id);
  };

  const closeBulkTariffModal = () => {
    if (bulkTariffLoading) return;
    setBulkTariffModalOpen(false);
    setBulkTariffTargetUnit(null);
    setBulkTariffCode('');
    setBulkTariffFieldError('');
    setBulkTariffSubmitError('');
    setBulkTariffOptions([]);
  };

  const navigateToAddPricingRule = useCallback(
    (unit) => {
      if (!unit?.id || !hasPermission(PERMISSIONS.PRICING_RULES_CREATE)) return;
      navigate('/PricingRules/Add', {
        state: {
          unitContext: {
            id: unit.id,
            serial_number: unit.serial_number ?? null,
          },
          returnTo: '/shipment/units',
        },
      });
    },
    [navigate, hasPermission]
  );

  const openRecalcConfirm = (unit) => {
    if (!unit?.id || !hasPermission(PERMISSIONS.SHIPMENTS_RECALCULATE_PRICE)) return;
    setRecalcTargetUnit(unit);
    setRecalcConfirmOpen(true);
  };

  const closeRecalcConfirm = () => {
    if (recalcLoading) return;
    setRecalcConfirmOpen(false);
    setRecalcTargetUnit(null);
  };

  const refreshDetailUnit = async (unitId) => {
    const res = await getShipmentUnit(unitId);
    setDetailUnit(res.data);
  };

  const handleRecalculateUnitPrices = async () => {
    if (!recalcTargetUnit?.id || !hasPermission(PERMISSIONS.SHIPMENTS_RECALCULATE_PRICE)) return;
    const unitId = recalcTargetUnit.id;
    setRecalcLoading(true);
    try {
      const res = await recalculateShipmentUnitPrices(unitId);
      const count = res.data?.recalculated_count ?? 0;
      setResponseMessage(t('shipmentUnit.recalcPricesSuccess', { count }));
      setSuccessNotification(true);
      setRecalcConfirmOpen(false);
      setRecalcTargetUnit(null);
      if (viewModalOpen && detailUnit?.id === unitId) {
        await refreshDetailUnit(unitId);
      }
    } catch (error) {
      setResponseMessage(masterApiErrorMessage(error, t) || t('shipmentUnit.recalcPricesFailed'));
      setFailedNotification(true);
    } finally {
      setRecalcLoading(false);
    }
  };

  const handleBulkUpdateTariff = async () => {
    if (!bulkTariffTargetUnit?.id || !hasPermission(PERMISSIONS.SHIPMENTS_UPDATE)) return;
    const trimmed = bulkTariffCode != null ? String(bulkTariffCode).trim() : '';
    if (trimmed.length > PRICING_TARIFF_CODE_MAX_LENGTH) {
      setBulkTariffFieldError(t('shipmentUnit.bulkTariffValMax', { max: PRICING_TARIFF_CODE_MAX_LENGTH }));
      return;
    }
    setBulkTariffFieldError('');
    setBulkTariffSubmitError('');
    const unitId = bulkTariffTargetUnit.id;
    setBulkTariffLoading(true);
    try {
      const res = await bulkUpdateShipmentUnitTariffCode(unitId, {
        pricing_tariff_code: trimmed || null,
        recalculate_prices: bulkTariffRecalculate,
      });
      const updatedCount = res.data?.updated_count ?? 0;
      let msg = t('shipmentUnit.bulkTariffSuccess', { count: updatedCount });
      if (bulkTariffRecalculate && res.data?.recalculated_count != null) {
        msg += ` ${t('shipmentUnit.bulkTariffRecalcSuccess', { count: res.data.recalculated_count })}`;
      }
      setResponseMessage(msg);
      setSuccessNotification(true);
      closeBulkTariffModal();
      if (viewModalOpen && detailUnit?.id === unitId) {
        await refreshDetailUnit(unitId);
      }
    } catch (error) {
      const res = error.response;
      if (res && res.status === 400 && res.data && typeof res.data === 'object') {
        const tcErr = res.data.pricing_tariff_code;
        if (tcErr != null) {
          setBulkTariffFieldError(fieldMessage(tcErr));
        } else {
          setBulkTariffSubmitError(masterApiErrorMessage(error, t) || t('shipmentUnit.bulkTariffFailed'));
        }
      } else {
        setBulkTariffSubmitError(masterApiErrorMessage(error, t) || t('shipmentUnit.bulkTariffFailed'));
      }
    } finally {
      setBulkTariffLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return t('shipmentUnit.dash');
    try {
      const d = new Date(dateString);
      return d.toLocaleString();
    } catch (e) {
      return dateString;
    }
  };

  const columns = [
      { field: 'id', headerName: t('shipmentUnit.colId'), flex: 0.6, minWidth: 70 },
      { field: 'serial_number', headerName: t('shipmentUnit.colSerial'), flex: 1.5, minWidth: 130 },
      {
        field: 'unit_number',
        headerName: t('shipmentUnit.colUnitNumber'),
        flex: 1.2,
        minWidth: 110,
        valueFormatter: (params) => {
          const v = params.value != null ? String(params.value).trim() : '';
          return v || t('shipmentUnit.dash');
        },
      },
      {
        field: 'event_type',
        headerName: t('shipmentUnit.colEventType'),
        flex: 1.2,
        minWidth: 120,
        renderCell: (params) => labelForEventTypeCode(params.row.event_type, eventTypeOptions),
      },
      { field: 'status', headerName: t('shipmentUnit.colStatus'), flex: 1, minWidth: 100 },
      { field: 'ship_name', headerName: t('shipmentUnit.colVessel'), flex: 1.2, minWidth: 120 },
      { field: 'expected_destination_eta', headerName: t('shipmentUnit.colEtaDestination'), flex: 1, minWidth: 200 },
      { field: 'expected_warehouse_eta', headerName: t('shipmentUnit.colEtaWarehouse'), flex: 1, minWidth: 200 },
      {
        field: 'destination_warehouse',
        headerName: t('shipmentUnit.colDestWarehouse'),
        flex: 0.8,
        minWidth: 110,
        renderCell: (params) =>
          masterFkDisplayLabel(params.row?.destination_warehouse, i18n.language, t('shipmentUnit.dash')),
      },
      {
        field: 'location',
        headerName: t('shipmentUnit.colLocation'),
        flex: 1.2,
        minWidth: 180,
        renderCell: (params) => {
          const s = getLocationDisplayNameFromRow(params.row, i18n.language);
          return s || t('shipmentUnit.dash');
        },
      },
      {
        field: 'actions',
        headerName: t('shipmentUnit.colActions'),
        flex: 1.8,
        minWidth: 260,
        sortable: false,
        renderCell: (params) => {
          if (params.row.deleted) {
            return null;
          }
          return (
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              {hasPermission(PERMISSIONS.SHIPMENT_UNITS_VIEW) && (
                <Tooltip title={t('shipmentUnit.tooltipView')}>
                  <IconButton size="sm" variant="soft" color="neutral" onClick={(e) => { e.stopPropagation(); openView(params.row); }}>
                    <VisibilityIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              {hasPermission(PERMISSIONS.PRICING_RULES_CREATE) && (
                <Tooltip title={t('shipmentUnit.tooltipAddPricingRule')}>
                  <IconButton
                    size="sm"
                    variant="soft"
                    color="warning"
                    onClick={(e) => { e.stopPropagation(); navigateToAddPricingRule(params.row); }}
                  >
                    <PriceChangeIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              {hasPermission(PERMISSIONS.SHIPMENTS_RECALCULATE_PRICE) && (
                <Tooltip title={t('shipmentUnit.tooltipRecalcPrices')}>
                  <IconButton
                    size="sm"
                    variant="soft"
                    color="success"
                    onClick={(e) => { e.stopPropagation(); openRecalcConfirm(params.row); }}
                  >
                    <CurrencyExchangeIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              {hasPermission(PERMISSIONS.SHIPMENTS_UPDATE) && (
                <Tooltip title={t('shipmentUnit.tooltipBulkUpdateTariff')}>
                  <IconButton
                    size="sm"
                    variant="soft"
                    color="warning"
                    onClick={(e) => { e.stopPropagation(); openBulkTariffModal(params.row); }}
                  >
                    <LocalOfferIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              {hasPermission(PERMISSIONS.SHIPMENT_UNITS_UPDATE) && (
                <Tooltip title={t('shipmentUnit.tooltipEdit')}>
                  <IconButton size="sm" variant="soft" color="primary" onClick={(e) => { e.stopPropagation(); openEdit(params.row); }}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              {hasPermission(PERMISSIONS.SHIPMENT_UNITS_DELETE) && (
                <Tooltip title={t('shipmentUnit.tooltipDeleteSoft')}>
                  <IconButton size="sm" variant="soft" color="danger" onClick={(e) => { e.stopPropagation(); setDeletingUnit(params.row); setDeleteModalOpen(true); }}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          );
        },
      },
  ];

  const rowCount = data && data.count != null ? data.count : units.length;

  return (
    <DirectionalMaterialThemeProvider>
      <CssBaseline />
      <form onSubmit={handleSubmitFilters}>
        <Box sx={{ display: 'flex' }} m={3}>
          <Box width="100%">
            <Box sx={{ mb: 3 }}>
              <Breadcrumbs separator={<KeyboardArrowRight />} aria-label="breadcrumbs">
                <Link color="neutral" href="/shipments">{t('nav.shipments')}</Link>
                <Typography>{t('nav.shipmentUnits')}</Typography>
              </Breadcrumbs>
              <Typography textAlign="start" level="h1" sx={{ fontSize: { xs: '1.5rem', sm: 'inherit' } }}>
                {t('nav.shipmentUnits')}
              </Typography>
              <Typography level="body-sm" sx={{ color: 'neutral.600', mt: 0.5 }}>
                {t('shipmentUnit.pageSubtitle')}
              </Typography>
            </Box>

            <PermissionGate
              permissions={[PERMISSIONS.SHIPMENT_UNITS_LIST]}
              fallback={<Typography sx={{ p: 2 }}>{t('shipmentUnit.noListPermission')}</Typography>}
            >
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
                <Typography level="title-md">{t('shipmentUnit.sectionUnits')}</Typography>
                {hasPermission(PERMISSIONS.SHIPMENT_UNITS_CREATE) && (
                  <Button
                    startDecorator={<AddIcon />}
                    onClick={() => {
                      setCreateForm(emptyForm);
                      setCreateCompanyOpt(null);
                      setCreateWarehouseOpt(null);
                      setCreateDestWarehouseOpt(null);
                      setCreateLocationOpt(null);
                      setCreateEventTypeOpt(null);
                      setCreateFieldErrors(emptyFieldErrors());
                      setCreateSubmitError('');
                      setCreateModalOpen(true);
                    }}
                  >
                    {t('shipmentUnit.newUnit')}
                  </Button>
                )}
              </Stack>

              <Grid
                container
                spacing={2}
                sx={{
                  flexGrow: 1,
                  mb: 2,
                  alignItems: 'stretch',
                  '& .filter-field': {
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%',
                    minWidth: 0,
                  },
                }}
              >
                <Grid xs={12} sm={6} lg={3}>
                  <Box className="filter-field" sx={{ mb: 2 }}>
                    <FormLabel
                      sx={{
                        fontWeight: 600,
                        color: '#555',
                        mb: '6px',
                        fontSize: '14px',
                      }}
                    >
                      {t('shipmentUnit.searchLabel')}
                    </FormLabel>
                    <Input
                      placeholder={t('shipmentUnit.searchPlaceholder')}
                      value={filters.search}
                      onChange={(e) => setFilters((prev) => ({ ...prev, search: e.target.value }))}
                      startDecorator={<SearchIcon />}
                      sx={{
                        backgroundColor: '#f7f9fc',
                        borderRadius: '8px',
                        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                      }}
                    />
                  </Box>
                </Grid>
                <Grid xs={12} sm={6} lg={3}>
                  <Box className="filter-field">
                    <ShippingCompanyOptionList
                      label={t('shipmentUnit.shippingCompany')}
                      defaultValue={filterShippingCompanyOpt}
                      onChange={(e, v) => setFilterShippingCompanyOpt(v)}
                    />
                  </Box>
                </Grid>
                <Grid xs={12} sm={6} lg={3}>
                  <Box className="filter-field">
                    <ShippingWarehouseOptionList
                      label={t('shipmentUnit.originWarehouse')}
                      defaultValue={filterOriginWarehouseOpt}
                      onChange={(e, v) => setFilterOriginWarehouseOpt(v)}
                    />
                  </Box>
                </Grid>
                <Grid xs={12} sm={6} lg={3}>
                  <Box className="filter-field">
                    <ShippingWarehouseOptionList
                      label={t('shipmentUnit.destinationWarehouse')}
                      defaultValue={filterDestinationWarehouseOpt}
                      onChange={(e, v) => setFilterDestinationWarehouseOpt(v)}
                    />
                  </Box>
                </Grid>
                <Grid xs={12} md={6} lg={7}>
                  <Box className="filter-field">
                    <LocationOptionList
                      label={t('shipmentUnit.colLocation')}
                      defaultValue={filterLocationOpt}
                      onChange={(e, v) => setFilterLocationOpt(v)}
                    />
                  </Box>
                </Grid>
                <Grid xs={12} sm={6} md={3} lg={2}>
                  <Box className="filter-field" sx={{ mb: 2 }}>
                    <FormLabel
                      sx={{
                        fontWeight: 600,
                        color: '#555',
                        mb: '6px',
                        fontSize: '14px',
                      }}
                    >
                      {t('shipmentUnit.deletedLabel')}
                    </FormLabel>
                    <Select
                      value={filters.deleted || null}
                      onChange={(_, v) => setFilters((prev) => ({ ...prev, deleted: v || '' }))}
                      sx={{
                        backgroundColor: '#f7f9fc',
                        borderRadius: '8px',
                        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                      }}
                    >
                      <Option value="">{t('shipmentUnit.deletedDefault')}</Option>
                      <Option value="false">{t('shipmentUnit.deletedNot')}</Option>
                      <Option value="true">{t('shipmentUnit.deletedYes')}</Option>
                    </Select>
                  </Box>
                </Grid>
                <Grid xs={12} sm={6} md={3} lg={3}>
                  <Box className="filter-field" sx={{ mb: 2 }}>
                    {/* Spacer matches option-list label block so the button lines up with inputs */}
                    <FormLabel
                      sx={{
                        fontWeight: 600,
                        fontSize: '14px',
                        mb: '6px',
                        lineHeight: 1.43,
                        visibility: 'hidden',
                        userSelect: 'none',
                      }}
                      aria-hidden
                    >
                      {t('shipmentUnit.applyFilters')}
                    </FormLabel>
                    <Button type="submit" fullWidth>
                      {t('shipmentUnit.applyFilters')}
                    </Button>
                  </Box>
                </Grid>
              </Grid>

              <Card variant="outlined" sx={{ borderRadius: '12px' }}>
                <CardContent sx={{ p: 0 }}>
                  <DataGrid
                    rows={units}
                    columns={columns}
                    loading={loading}
                    getRowId={(r) => r.id}
                    paginationMode="server"
                    rowCount={rowCount}
                    pageSizeOptions={[25, 50, 100]}
                    paginationModel={paginationModel}
                    onPaginationModelChange={setPaginationModel}
                    disableRowSelectionOnClick
                    autoHeight
                    sx={{ border: 'none' }}
                  />
                </CardContent>
              </Card>
            </PermissionGate>

            <Snackbar open={successNotification} color="success" variant="soft" onClose={() => setSuccessNotification(false)} autoHideDuration={4000}>
              {responseMessage}
            </Snackbar>
            <Snackbar open={failedNotification} color="danger" variant="soft" onClose={() => setFailedNotification(false)} autoHideDuration={6000}>
              {responseMessage}
            </Snackbar>
          </Box>
        </Box>
      </form>

      <Modal
        open={createModalOpen}
        onClose={() => {
          setCreateModalOpen(false);
          setCreateFieldErrors(emptyFieldErrors());
          setCreateSubmitError('');
        }}
      >
        <ModalDialog
          sx={{
            maxWidth: 560,
            width: '100%',
            boxSizing: 'border-box',
            maxHeight: '90vh',
            overflowY: 'auto',
            overflowX: 'hidden',
          }}
        >
          <ModalClose />
          <Typography level="title-lg" sx={{ mb: 2 }}>{t('shipmentUnit.modalCreateTitle')}</Typography>
          <Stack spacing={1.5} sx={{ minWidth: 0, width: '100%', overflowX: 'hidden' }}>
            {createSubmitError ? (
              <Box ref={createSubmitBannerRef}>
                <Typography level="body-sm" color="danger" sx={{ p: 1.5, borderRadius: 'sm', bgcolor: 'danger.softBg' }}>
                  {createSubmitError}
                </Typography>
              </Box>
            ) : null}
            <Box ref={(el) => { createErrorAnchorsRef.current.serial_number = el; }}>
            <FormControl required error={!!createFieldErrors.serial_number}>
              <FormLabel>{t('shipmentUnit.serialNumber')}</FormLabel>
              <Input
                value={createForm.serial_number}
                onChange={(e) => {
                  const v = e.target.value;
                  setCreateForm((p) => ({ ...p, serial_number: v }));
                  setCreateFieldErrors((prev) => ({ ...prev, serial_number: '' }));
                  setCreateSubmitError('');
                }}
              />
              {createFieldErrors.serial_number ? (
                <FormHelperText sx={{ color: 'danger.500' }}>{createFieldErrors.serial_number}</FormHelperText>
              ) : null}
            </FormControl>
            </Box>
            <Box ref={(el) => { createErrorAnchorsRef.current.unit_number = el; }}>
            <FormControl error={!!createFieldErrors.unit_number}>
              <FormLabel>{t('shipmentUnit.unitNumber')}</FormLabel>
              <Input
                value={createForm.unit_number}
                slotProps={{ input: { maxLength: UNIT_NUMBER_MAX_LENGTH } }}
                onChange={(e) => {
                  const v = e.target.value;
                  setCreateForm((p) => ({ ...p, unit_number: v }));
                  setCreateFieldErrors((prev) => ({ ...prev, unit_number: '' }));
                  setCreateSubmitError('');
                }}
              />
              <Typography level="body-xs" sx={{ color: 'neutral.600', mt: 0.5 }}>
                {t('shipmentUnit.unitNumberHint')}
              </Typography>
              {createFieldErrors.unit_number ? (
                <FormHelperText sx={{ color: 'danger.500' }}>{createFieldErrors.unit_number}</FormHelperText>
              ) : null}
            </FormControl>
            </Box>
            <Box ref={(el) => { createErrorAnchorsRef.current.shipments_ids_text = el; }}>
            <FormControl error={!!createFieldErrors.shipments_ids_text}>
              <FormLabel>{t('shipmentUnit.shipmentIdsComma')}</FormLabel>
              <Input
                placeholder={t('shipmentUnit.shipmentIdsPlaceholder')}
                value={createForm.shipments_ids_text}
                onChange={(e) => {
                  setCreateForm((p) => ({ ...p, shipments_ids_text: e.target.value }));
                  setCreateFieldErrors((prev) => ({ ...prev, shipments_ids_text: '' }));
                  setCreateSubmitError('');
                }}
              />
              {createFieldErrors.shipments_ids_text ? (
                <FormHelperText sx={{ color: 'danger.500' }}>{createFieldErrors.shipments_ids_text}</FormHelperText>
              ) : null}
            </FormControl>
            </Box>
            <Box ref={(el) => { createErrorAnchorsRef.current.shipped_at = el; }}>
            <FormControl error={!!createFieldErrors.shipped_at}>
              <FormLabel>{t('shipmentUnit.shippedAt')}</FormLabel>
              <Input
                type="datetime-local"
                value={createForm.shipped_at}
                onChange={(e) => {
                  setCreateForm((p) => ({ ...p, shipped_at: e.target.value }));
                  setCreateFieldErrors((prev) => ({ ...prev, shipped_at: '' }));
                  setCreateSubmitError('');
                }}
                slotProps={{
                  input: {
                    type: 'datetime-local',
                    step: 60,
                  },
                }}
                sx={{ width: '100%' }}
              />
              {createFieldErrors.shipped_at ? (
                <FormHelperText sx={{ color: 'danger.500' }}>{createFieldErrors.shipped_at}</FormHelperText>
              ) : null}
            </FormControl>
            </Box>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              gap={1}
              sx={{ '& > *': { flex: 1, minWidth: 0, mb: '0 !important' } }}
            >
              <Box ref={(el) => { createErrorAnchorsRef.current.shipping_company = el; }}>
              <ShippingCompanyOptionList
                label={t('shipmentUnit.shippingCompany')}
                defaultValue={createCompanyOpt}
                errorMessage={createFieldErrors.shipping_company || undefined}
                onChange={(e, v) => {
                  setCreateCompanyOpt(v);
                  setCreateForm((p) => ({
                    ...p,
                    shipping_company: v && v.id != null ? String(v.id) : '',
                  }));
                  setCreateFieldErrors((prev) => ({ ...prev, shipping_company: '' }));
                  setCreateSubmitError('');
                }}
              />
              </Box>
              <Box ref={(el) => { createErrorAnchorsRef.current.origin_warehouse = el; }}>
              <ShippingWarehouseOptionList
                label={t('shipmentUnit.originWarehouse')}
                defaultValue={createWarehouseOpt}
                errorMessage={createFieldErrors.origin_warehouse || undefined}
                onChange={(e, v) => {
                  setCreateWarehouseOpt(v);
                  setCreateForm((p) => ({
                    ...p,
                    origin_warehouse: v && v.id != null ? String(v.id) : '',
                  }));
                  setCreateFieldErrors((prev) => ({ ...prev, origin_warehouse: '' }));
                  setCreateSubmitError('');
                }}
              />
              </Box>
            </Stack>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              gap={1}
              sx={{ '& > *': { flex: 1, minWidth: 0, mb: '0 !important' } }}
            >
              <Box ref={(el) => { createErrorAnchorsRef.current.destination_warehouse = el; }}>
                <ShippingWarehouseOptionList
                  label={t('shipmentUnit.destinationWarehouse')}
                  defaultValue={createDestWarehouseOpt}
                  errorMessage={createFieldErrors.destination_warehouse || undefined}
                  onChange={(e, v) => {
                    setCreateDestWarehouseOpt(v);
                    setCreateForm((p) => ({
                      ...p,
                      destination_warehouse: v && v.id != null ? String(v.id) : '',
                    }));
                    setCreateFieldErrors((prev) => ({ ...prev, destination_warehouse: '' }));
                    setCreateSubmitError('');
                  }}
                />
              </Box>
              <Box
                ref={(el) => {
                  createErrorAnchorsRef.current.location = el;
                }}
                sx={{ minWidth: 0, maxWidth: '100%' }}
              >
                <LocationOptionList
                  required
                  label={t('shipmentUnit.currentLocation')}
                  defaultValue={createLocationOpt}
                  errorMessage={createFieldErrors.location || undefined}
                  onChange={(e, v) => {
                    setCreateLocationOpt(v);
                    setCreateForm((p) => ({
                      ...p,
                      location: v && v.id != null ? String(v.id) : '',
                    }));
                    setCreateFieldErrors((prev) => ({ ...prev, location: '' }));
                    setCreateSubmitError('');
                  }}
                />
              </Box>
            </Stack>
            <Box ref={(el) => { createErrorAnchorsRef.current.tracking_url = el; }}>
            <FormControl error={!!createFieldErrors.tracking_url}>
              <FormLabel>{t('shipmentUnit.trackingUrl')}</FormLabel>
              <Input
                value={createForm.tracking_url}
                onChange={(e) => {
                  setCreateForm((p) => ({ ...p, tracking_url: e.target.value }));
                  setCreateFieldErrors((prev) => ({ ...prev, tracking_url: '' }));
                  setCreateSubmitError('');
                }}
              />
              {createFieldErrors.tracking_url ? (
                <FormHelperText sx={{ color: 'danger.500' }}>{createFieldErrors.tracking_url}</FormHelperText>
              ) : null}
            </FormControl>
            </Box>
            <Box ref={(el) => { createErrorAnchorsRef.current.expected_dates = el; }}>
              <Stack direction="row" gap={1} sx={{ '& > *': { minWidth: 0 } }}>
                <FormControl sx={{ flex: 1 }} error={!!(createFieldErrors.expected_destination_eta || createFieldErrors.eta_order)}>
                  <FormLabel>{t('shipmentUnit.expectedArrivalDestination')}</FormLabel>
                  <Input
                    type="date"
                    value={createForm.expected_destination_eta}
                    onChange={(e) => {
                      const v = e.target.value;
                      setCreateForm((p) => ({ ...p, expected_destination_eta: v }));
                      setCreateFieldErrors((prev) => ({
                        ...prev,
                        expected_destination_eta: '',
                        eta_order: '',
                      }));
                      setCreateSubmitError('');
                    }}
                  />
                  {createFieldErrors.expected_destination_eta ? (
                    <FormHelperText sx={{ color: 'danger.500' }}>{createFieldErrors.expected_destination_eta}</FormHelperText>
                  ) : null}
                </FormControl>
                <FormControl sx={{ flex: 1 }} error={!!(createFieldErrors.expected_warehouse_eta || createFieldErrors.eta_order)}>
                  <FormLabel>{t('shipmentUnit.expectedArrivalWarehouse')}</FormLabel>
                  <Input
                    type="date"
                    value={createForm.expected_warehouse_eta}
                    onChange={(e) => {
                      const v = e.target.value;
                      setCreateForm((p) => ({ ...p, expected_warehouse_eta: v }));
                      setCreateFieldErrors((prev) => ({
                        ...prev,
                        expected_warehouse_eta: '',
                        eta_order: '',
                      }));
                      setCreateSubmitError('');
                    }}
                  />
                  {createFieldErrors.expected_warehouse_eta ? (
                    <FormHelperText sx={{ color: 'danger.500' }}>{createFieldErrors.expected_warehouse_eta}</FormHelperText>
                  ) : null}
                </FormControl>
              </Stack>
              {createFieldErrors.eta_order ? (
                <FormHelperText sx={{ color: 'danger.500', mt: 0.5 }}>{createFieldErrors.eta_order}</FormHelperText>
              ) : null}
            </Box>
            <Box ref={(el) => { createErrorAnchorsRef.current.ship_name = el; }}>
            <FormControl error={!!createFieldErrors.ship_name}>
              <FormLabel>{t('shipmentUnit.vesselName')}</FormLabel>
              <Input
                value={createForm.ship_name}
                onChange={(e) => {
                  setCreateForm((p) => ({ ...p, ship_name: e.target.value }));
                  setCreateFieldErrors((prev) => ({ ...prev, ship_name: '' }));
                  setCreateSubmitError('');
                }}
              />
              {createFieldErrors.ship_name ? (
                <FormHelperText sx={{ color: 'danger.500' }}>{createFieldErrors.ship_name}</FormHelperText>
              ) : null}
            </FormControl>
            </Box>
            <Box ref={(el) => { createErrorAnchorsRef.current.status = el; }}>
              <StatusOptionList
                label={t('actions.status')}
                required
                errorMessage={createFieldErrors.status || undefined}
                status={createForm.status}
                setStatus={(v) => {
                  setCreateForm((p) => ({ ...p, status: v || '' }));
                  setCreateFieldErrors((prev) => ({ ...prev, status: '' }));
                  setCreateSubmitError('');
                }}
              />
            </Box>
            <Box ref={(el) => { createErrorAnchorsRef.current.event_type = el; }}>
            <TrackingEventTypeOptionList
              label={t('shipmentUnit.eventType')}
              defaultValue={createEventTypeOpt}
              errorMessage={createFieldErrors.event_type || undefined}
              onChange={(e, v) => {
                setCreateEventTypeOpt(v);
                setCreateForm((p) => ({ ...p, event_type: (v && v.code) || '' }));
                setCreateFieldErrors((prev) => ({ ...prev, event_type: '' }));
                setCreateSubmitError('');
              }}
            />
            <Typography level="body-xs" sx={{ color: 'neutral.600', mt: 0.5 }}>
              {t('shipmentUnit.eventTypeHintCreate')}
            </Typography>
            </Box>
            <Box ref={(el) => { createErrorAnchorsRef.current.tracking_event_timestamp = el; }}>
              <TrackingEventControlFields
                mode={createForm.tracking_event_mode}
                onModeChange={(v) => {
                  setCreateForm((p) => ({ ...p, tracking_event_mode: v }));
                  setCreateFieldErrors((prev) => ({ ...prev, tracking_event_timestamp: '' }));
                  setCreateSubmitError('');
                }}
                timestampLocal={createForm.tracking_event_timestamp}
                onTimestampChange={(v) => {
                  setCreateForm((p) => ({ ...p, tracking_event_timestamp: v }));
                  setCreateFieldErrors((prev) => ({ ...prev, tracking_event_timestamp: '' }));
                  setCreateSubmitError('');
                }}
                disabled={loading}
                timestampError={createFieldErrors.tracking_event_timestamp}
              />
            </Box>
            <Box ref={(el) => { createErrorAnchorsRef.current.note = el; }}>
            <FormControl error={!!createFieldErrors.note}>
              <FormLabel>{t('shipmentUnit.note')}</FormLabel>
              <Textarea
                value={createForm.note}
                onChange={(e) => {
                  setCreateForm((p) => ({ ...p, note: e.target.value }));
                  setCreateFieldErrors((prev) => ({ ...prev, note: '' }));
                  setCreateSubmitError('');
                }}
                minRows={2}
              />
              {createFieldErrors.note ? (
                <FormHelperText sx={{ color: 'danger.500' }}>{createFieldErrors.note}</FormHelperText>
              ) : null}
            </FormControl>
            </Box>
            <Stack direction="row" gap={1} justifyContent="flex-end" sx={{ pt: 1 }}>
              <Button
                variant="plain"
                color="neutral"
                onClick={() => {
                  setCreateModalOpen(false);
                  setCreateFieldErrors(emptyFieldErrors());
                  setCreateSubmitError('');
                }}
              >
                {t('shipmentUnit.cancel')}
              </Button>
              <Button loading={loading} onClick={handleCreate}>{t('shipmentUnit.create')}</Button>
            </Stack>
          </Stack>
        </ModalDialog>
      </Modal>

      <Modal
        open={editModalOpen}
        onClose={() => {
          setEditModalOpen(false);
          setEditFieldErrors(emptyFieldErrors());
          setEditSubmitError('');
        }}
      >
        <ModalDialog
          sx={{
            maxWidth: 560,
            width: '100%',
            boxSizing: 'border-box',
            maxHeight: '90vh',
            overflowY: 'auto',
            overflowX: 'hidden',
          }}
        >
          <ModalClose />
          <Typography level="title-lg" sx={{ mb: 2 }}>{t('shipmentUnit.modalEditTitle', { id: editingId })}</Typography>
          <Stack spacing={1.5} sx={{ minWidth: 0, width: '100%', overflowX: 'hidden' }}>
            {editSubmitError ? (
              <Box ref={editSubmitBannerRef}>
                <Typography level="body-sm" color="danger" sx={{ p: 1.5, borderRadius: 'sm', bgcolor: 'danger.softBg' }}>
                  {editSubmitError}
                </Typography>
              </Box>
            ) : null}
            <Alert variant="soft" color="neutral" sx={{ maxWidth: '100%', boxSizing: 'border-box' }}>
              <Typography level="body-sm" sx={{ wordBreak: 'break-word' }}>
                {t('shipmentUnit.editSaveHint')}
              </Typography>
            </Alert>
            <Box ref={(el) => { editErrorAnchorsRef.current.serial_number = el; }}>
            <FormControl required error={!!editFieldErrors.serial_number}>
              <FormLabel>{t('shipmentUnit.serialNumber')}</FormLabel>
              <Input
                value={editForm.serial_number}
                onChange={(e) => {
                  setEditForm((p) => ({ ...p, serial_number: e.target.value }));
                  setEditFieldErrors((prev) => ({ ...prev, serial_number: '' }));
                  setEditSubmitError('');
                }}
              />
              {editFieldErrors.serial_number ? (
                <FormHelperText sx={{ color: 'danger.500' }}>{editFieldErrors.serial_number}</FormHelperText>
              ) : null}
            </FormControl>
            </Box>
            <Box ref={(el) => { editErrorAnchorsRef.current.unit_number = el; }}>
            <FormControl error={!!editFieldErrors.unit_number}>
              <FormLabel>{t('shipmentUnit.unitNumber')}</FormLabel>
              <Input
                value={editForm.unit_number}
                slotProps={{ input: { maxLength: UNIT_NUMBER_MAX_LENGTH } }}
                onChange={(e) => {
                  const v = e.target.value;
                  setEditForm((p) => ({ ...p, unit_number: v }));
                  setEditFieldErrors((prev) => ({ ...prev, unit_number: '' }));
                  setEditSubmitError('');
                }}
              />
              <Typography level="body-xs" sx={{ color: 'neutral.600', mt: 0.5 }}>
                {t('shipmentUnit.unitNumberHint')}
              </Typography>
              {editFieldErrors.unit_number ? (
                <FormHelperText sx={{ color: 'danger.500' }}>{editFieldErrors.unit_number}</FormHelperText>
              ) : null}
            </FormControl>
            </Box>
            <Box ref={(el) => { editErrorAnchorsRef.current.shipments_ids_text = el; }}>
            <FormControl error={!!editFieldErrors.shipments_ids_text}>
              <FormLabel>{t('shipmentUnit.shipmentIdsComma')}</FormLabel>
              <Input
                value={editForm.shipments_ids_text}
                onChange={(e) => {
                  setEditForm((p) => ({ ...p, shipments_ids_text: e.target.value }));
                  setEditFieldErrors((prev) => ({ ...prev, shipments_ids_text: '' }));
                  setEditSubmitError('');
                }}
              />
              {editFieldErrors.shipments_ids_text ? (
                <FormHelperText sx={{ color: 'danger.500' }}>{editFieldErrors.shipments_ids_text}</FormHelperText>
              ) : null}
            </FormControl>
            </Box>
            <Box ref={(el) => { editErrorAnchorsRef.current.shipped_at = el; }}>
            <FormControl error={!!editFieldErrors.shipped_at}>
              <FormLabel>{t('shipmentUnit.shippedAt')}</FormLabel>
              <Input
                type="datetime-local"
                value={editForm.shipped_at}
                onChange={(e) => {
                  setEditForm((p) => ({ ...p, shipped_at: e.target.value }));
                  setEditFieldErrors((prev) => ({ ...prev, shipped_at: '' }));
                  setEditSubmitError('');
                }}
                slotProps={{
                  input: {
                    type: 'datetime-local',
                    step: 60,
                  },
                }}
                sx={{ width: '100%' }}
              />
              {editFieldErrors.shipped_at ? (
                <FormHelperText sx={{ color: 'danger.500' }}>{editFieldErrors.shipped_at}</FormHelperText>
              ) : null}
            </FormControl>
            </Box>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              gap={1}
              sx={{ '& > *': { flex: 1, minWidth: 0, mb: '0 !important' } }}
            >
              <Box ref={(el) => { editErrorAnchorsRef.current.shipping_company = el; }}>
              <ShippingCompanyOptionList
                label={t('shipmentUnit.shippingCompany')}
                defaultValue={editCompanyOpt}
                errorMessage={editFieldErrors.shipping_company || undefined}
                onChange={(e, v) => {
                  setEditCompanyOpt(v);
                  setEditForm((p) => ({
                    ...p,
                    shipping_company: v && v.id != null ? String(v.id) : '',
                  }));
                  setEditFieldErrors((prev) => ({ ...prev, shipping_company: '' }));
                  setEditSubmitError('');
                }}
              />
              </Box>
              <Box ref={(el) => { editErrorAnchorsRef.current.origin_warehouse = el; }}>
              <ShippingWarehouseOptionList
                label={t('shipmentUnit.originWarehouse')}
                defaultValue={editWarehouseOpt}
                errorMessage={editFieldErrors.origin_warehouse || undefined}
                onChange={(e, v) => {
                  setEditWarehouseOpt(v);
                  setEditForm((p) => ({
                    ...p,
                    origin_warehouse: v && v.id != null ? String(v.id) : '',
                  }));
                  setEditFieldErrors((prev) => ({ ...prev, origin_warehouse: '' }));
                  setEditSubmitError('');
                }}
              />
              </Box>
            </Stack>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              gap={1}
              sx={{ '& > *': { flex: 1, minWidth: 0, mb: '0 !important' } }}
            >
              <Box ref={(el) => { editErrorAnchorsRef.current.destination_warehouse = el; }}>
                <ShippingWarehouseOptionList
                  label={t('shipmentUnit.destinationWarehouse')}
                  defaultValue={editDestWarehouseOpt}
                  errorMessage={editFieldErrors.destination_warehouse || undefined}
                  onChange={(e, v) => {
                    setEditDestWarehouseOpt(v);
                    setEditForm((p) => ({
                      ...p,
                      destination_warehouse: v && v.id != null ? String(v.id) : '',
                    }));
                    setEditFieldErrors((prev) => ({ ...prev, destination_warehouse: '' }));
                    setEditSubmitError('');
                  }}
                />
              </Box>
              <Box
                ref={(el) => {
                  editErrorAnchorsRef.current.location = el;
                }}
                sx={{ minWidth: 0, maxWidth: '100%' }}
              >
                <LocationOptionList
                  required
                  label={t('shipmentUnit.currentLocation')}
                  defaultValue={editLocationOpt}
                  errorMessage={editFieldErrors.location || undefined}
                  onChange={(e, v) => {
                    setEditLocationOpt(v);
                    setEditForm((p) => ({
                      ...p,
                      location: v && v.id != null ? String(v.id) : '',
                    }));
                    setEditFieldErrors((prev) => ({ ...prev, location: '' }));
                    setEditSubmitError('');
                  }}
                />
              </Box>
            </Stack>
            <Box ref={(el) => { editErrorAnchorsRef.current.tracking_url = el; }}>
            <FormControl error={!!editFieldErrors.tracking_url}>
              <FormLabel>{t('shipmentUnit.trackingUrl')}</FormLabel>
              <Input
                value={editForm.tracking_url}
                onChange={(e) => {
                  setEditForm((p) => ({ ...p, tracking_url: e.target.value }));
                  setEditFieldErrors((prev) => ({ ...prev, tracking_url: '' }));
                  setEditSubmitError('');
                }}
              />
              {editFieldErrors.tracking_url ? (
                <FormHelperText sx={{ color: 'danger.500' }}>{editFieldErrors.tracking_url}</FormHelperText>
              ) : null}
            </FormControl>
            </Box>
            <Box ref={(el) => { editErrorAnchorsRef.current.expected_dates = el; }}>
              <Stack direction="row" gap={1} sx={{ '& > *': { minWidth: 0 } }}>
                <FormControl sx={{ flex: 1 }} error={!!(editFieldErrors.expected_destination_eta || editFieldErrors.eta_order)}>
                  <FormLabel>{t('shipmentUnit.expectedArrivalDestination')}</FormLabel>
                  <Input
                    type="date"
                    value={editForm.expected_destination_eta}
                    onChange={(e) => {
                      const v = e.target.value;
                      setEditForm((p) => ({ ...p, expected_destination_eta: v }));
                      setEditFieldErrors((prev) => ({
                        ...prev,
                        expected_destination_eta: '',
                        eta_order: '',
                      }));
                      setEditSubmitError('');
                    }}
                  />
                  {editFieldErrors.expected_destination_eta ? (
                    <FormHelperText sx={{ color: 'danger.500' }}>{editFieldErrors.expected_destination_eta}</FormHelperText>
                  ) : null}
                </FormControl>
                <FormControl sx={{ flex: 1 }} error={!!(editFieldErrors.expected_warehouse_eta || editFieldErrors.eta_order)}>
                  <FormLabel>{t('shipmentUnit.expectedArrivalWarehouse')}</FormLabel>
                  <Input
                    type="date"
                    value={editForm.expected_warehouse_eta}
                    onChange={(e) => {
                      const v = e.target.value;
                      setEditForm((p) => ({ ...p, expected_warehouse_eta: v }));
                      setEditFieldErrors((prev) => ({
                        ...prev,
                        expected_warehouse_eta: '',
                        eta_order: '',
                      }));
                      setEditSubmitError('');
                    }}
                  />
                  {editFieldErrors.expected_warehouse_eta ? (
                    <FormHelperText sx={{ color: 'danger.500' }}>{editFieldErrors.expected_warehouse_eta}</FormHelperText>
                  ) : null}
                </FormControl>
              </Stack>
              {editFieldErrors.eta_order ? (
                <FormHelperText sx={{ color: 'danger.500', mt: 0.5 }}>{editFieldErrors.eta_order}</FormHelperText>
              ) : null}
            </Box>
            <Box ref={(el) => { editErrorAnchorsRef.current.ship_name = el; }}>
            <FormControl error={!!editFieldErrors.ship_name}>
              <FormLabel>{t('shipmentUnit.vesselName')}</FormLabel>
              <Input
                value={editForm.ship_name}
                onChange={(e) => {
                  setEditForm((p) => ({ ...p, ship_name: e.target.value }));
                  setEditFieldErrors((prev) => ({ ...prev, ship_name: '' }));
                  setEditSubmitError('');
                }}
              />
              {editFieldErrors.ship_name ? (
                <FormHelperText sx={{ color: 'danger.500' }}>{editFieldErrors.ship_name}</FormHelperText>
              ) : null}
            </FormControl>
            </Box>
            <Box ref={(el) => { editErrorAnchorsRef.current.status = el; }}>
              <StatusOptionList
                label={t('actions.status')}
                required
                errorMessage={editFieldErrors.status || undefined}
                status={editForm.status}
                setStatus={(v) => {
                  setEditForm((p) => ({ ...p, status: v || '' }));
                  setEditFieldErrors((prev) => ({ ...prev, status: '' }));
                  setEditSubmitError('');
                }}
              />
            </Box>
            <Box ref={(el) => { editErrorAnchorsRef.current.event_type = el; }}>
            <TrackingEventTypeOptionList
              label={t('shipmentUnit.eventType')}
              defaultValue={editEventTypeOpt}
              errorMessage={editFieldErrors.event_type || undefined}
              onChange={(e, v) => {
                setEditEventTypeOpt(v);
                setEditForm((p) => ({ ...p, event_type: (v && v.code) || '' }));
                setEditFieldErrors((prev) => ({ ...prev, event_type: '' }));
                setEditSubmitError('');
              }}
            />
            <Typography level="body-xs" sx={{ color: 'neutral.600', mt: 0.5 }}>
              {t('shipmentUnit.eventTypeHintEdit')}
            </Typography>
            </Box>
            <Box ref={(el) => { editErrorAnchorsRef.current.tracking_event_timestamp = el; }}>
              <TrackingEventControlFields
                mode={editForm.tracking_event_mode}
                onModeChange={(v) => {
                  setEditForm((p) => ({ ...p, tracking_event_mode: v }));
                  setEditFieldErrors((prev) => ({ ...prev, tracking_event_timestamp: '' }));
                  setEditSubmitError('');
                }}
                timestampLocal={editForm.tracking_event_timestamp}
                onTimestampChange={(v) => {
                  setEditForm((p) => ({ ...p, tracking_event_timestamp: v }));
                  setEditFieldErrors((prev) => ({ ...prev, tracking_event_timestamp: '' }));
                  setEditSubmitError('');
                }}
                disabled={loading}
                timestampError={editFieldErrors.tracking_event_timestamp}
              />
            </Box>
            <Box ref={(el) => { editErrorAnchorsRef.current.note = el; }}>
            <FormControl error={!!editFieldErrors.note}>
              <FormLabel>{t('shipmentUnit.note')}</FormLabel>
              <Textarea
                value={editForm.note}
                onChange={(e) => {
                  setEditForm((p) => ({ ...p, note: e.target.value }));
                  setEditFieldErrors((prev) => ({ ...prev, note: '' }));
                  setEditSubmitError('');
                }}
                minRows={2}
              />
              {editFieldErrors.note ? (
                <FormHelperText sx={{ color: 'danger.500' }}>{editFieldErrors.note}</FormHelperText>
              ) : null}
            </FormControl>
            </Box>
            <Box>
              <NativeCheckbox
                checked={!!editForm.notify_owners_whatsapp}
                disabled={!hasPermission(PERMISSIONS.WHATSAPP_SEND_SHIPMENT_NOTIFICATION)}
                onChange={(e) => {
                  setEditForm((p) => ({ ...p, notify_owners_whatsapp: e.target.checked }));
                  setEditSubmitError('');
                }}
                label={t('shipmentUnit.notifyWhatsapp')}
              />
              <Typography level="body-xs" sx={{ color: 'neutral.600', mt: 0.5, pl: 3.5 }}>
                {t('shipmentUnit.notifyWhatsappHint')}
              </Typography>
            </Box>
            <Stack direction="row" gap={1} justifyContent="flex-end" sx={{ pt: 1 }}>
              <Button
                variant="plain"
                color="neutral"
                onClick={() => {
                  setEditModalOpen(false);
                  setEditFieldErrors(emptyFieldErrors());
                  setEditSubmitError('');
                }}
              >
                {t('shipmentUnit.cancel')}
              </Button>
              <Button loading={loading} onClick={handleUpdate}>{t('shipmentUnit.save')}</Button>
            </Stack>
          </Stack>
        </ModalDialog>
      </Modal>

      <Modal open={viewModalOpen} onClose={() => { setViewModalOpen(false); closeRecalcConfirm(); closeBulkTariffModal(); }}>
        <ModalDialog sx={{ maxWidth: 720, maxHeight: '90vh', overflow: 'auto' }}>
          <ModalClose />
          <Typography level="title-lg" sx={{ mb: 1 }}>{detailUnit && t('shipmentUnit.viewUnitTitle', { id: detailUnit.id })}</Typography>
          {detailUnit && (
            <>
              <Typography level="body-sm" sx={{ mb: 2 }}>{t('shipmentUnit.viewSerial', { serial: detailUnit.serial_number || '' })}</Typography>
              <Stack spacing={0.5} sx={{ mb: 2 }}>
                <Typography level="body-sm">
                  {t('shipmentUnit.viewUnitNumber', {
                    unitNumber:
                      detailUnit.unit_number != null && String(detailUnit.unit_number).trim()
                        ? detailUnit.unit_number
                        : t('shipmentUnit.dash'),
                  })}
                </Typography>
                <Typography level="body-sm">
                  {t('shipmentUnit.viewShipments')}{' '}
                  {Array.isArray(detailUnit.shipments) && detailUnit.shipments.length
                    ? detailUnit.shipments
                        .map((s) => (typeof s === 'object' && s != null ? s.id : s))
                        .filter((id) => id != null)
                        .join(', ')
                    : t('shipmentUnit.dash')}
                </Typography>
                <Typography level="body-sm">
                  {t('shipmentUnit.viewOriginWarehouseId')}{' '}
                  {masterFkDisplayLabel(detailUnit.origin_warehouse, i18n.language, t('shipmentUnit.dash'))}
                </Typography>
                <Typography level="body-sm">
                  {t('shipmentUnit.viewDestWarehouseId')}{' '}
                  {masterFkDisplayLabel(detailUnit.destination_warehouse, i18n.language, t('shipmentUnit.dash'))}
                </Typography>
                <Typography level="body-sm">
                  {t('shipmentUnit.viewLocation')}{' '}
                  {typeof detailUnit.location === 'string' && detailUnit.location.trim()
                    ? detailUnit.location
                    : t('shipmentUnit.dash')}
                </Typography>
              </Stack>
              <Divider sx={{ my: 1 }} />
              <Typography level="title-sm" sx={{ mb: 1 }}>{t('shipmentUnit.timelineNested')}</Typography>
              {Array.isArray(detailUnit.tracking_events) && detailUnit.tracking_events.length > 0 ? (
                <Stack spacing={1}>
                  {detailUnit.tracking_events.map((ev) => (
                    <Box key={ev.id} sx={{ p: 1, bgcolor: 'background.level1', borderRadius: 'sm' }}>
                      <Typography level="body-sm" fontWeight="lg">{eventTypeDisplayFromRow(ev, eventTypeOptions)}</Typography>
                      <Typography level="body-xs" sx={{ color: 'neutral.600' }}>{formatDate(ev.timestamp)}</Typography>
                      {ev.description && <Typography level="body-sm">{ev.description}</Typography>}
                    </Box>
                  ))}
                </Stack>
              ) : (
                <Typography level="body-sm">{t('shipmentUnit.noUnitTrackingEvents')}</Typography>
              )}
              <UnitPricingRulesPanel unit={detailUnit} onAddRule={navigateToAddPricingRule} />
              <Stack direction="row" justifyContent="flex-end" gap={1} sx={{ mt: 2, flexWrap: 'wrap' }}>
                {hasPermission(PERMISSIONS.SHIPMENTS_UPDATE) && (
                  <Button
                    variant="soft"
                    color="warning"
                    loading={bulkTariffLoading && bulkTariffTargetUnit?.id === detailUnit.id}
                    onClick={() => openBulkTariffModal(detailUnit)}
                  >
                    {t('shipmentUnit.bulkUpdateTariff')}
                  </Button>
                )}
                {hasPermission(PERMISSIONS.SHIPMENTS_RECALCULATE_PRICE) && (
                  <Button
                    variant="soft"
                    color="success"
                    loading={recalcLoading && recalcTargetUnit?.id === detailUnit.id}
                    onClick={() => openRecalcConfirm(detailUnit)}
                  >
                    {t('shipmentUnit.recalcAllPrices')}
                  </Button>
                )}
              </Stack>
            </>
          )}
        </ModalDialog>
      </Modal>

      <Modal open={bulkTariffModalOpen} onClose={closeBulkTariffModal}>
        <ModalDialog variant="outlined" sx={{ maxWidth: 480, width: '100%' }}>
          <ModalClose />
          <Typography level="title-lg">{t('shipmentUnit.bulkTariffModalTitle')}</Typography>
          {bulkTariffTargetUnit && (
            <Typography level="body-sm" sx={{ mt: 0.5, color: 'neutral.600' }}>
              {t('shipmentUnit.bulkTariffModalUnit', {
                id: bulkTariffTargetUnit.id,
                serial: bulkTariffTargetUnit.serial_number || t('shipmentUnit.dash'),
              })}
            </Typography>
          )}
          <Typography level="body-sm" sx={{ mt: 1 }}>
            {bulkTariffShipmentCount == null
              ? t('shipmentUnit.bulkTariffConfirmBodyUnknown')
              : bulkTariffShipmentCount > 10
                ? t('shipmentUnit.bulkTariffConfirmBodyMany', { count: bulkTariffShipmentCount })
                : t('shipmentUnit.bulkTariffConfirmBody', { count: bulkTariffShipmentCount })}
          </Typography>
          {bulkTariffSubmitError ? (
            <Typography level="body-sm" color="danger" sx={{ mt: 1, p: 1.5, borderRadius: 'sm', bgcolor: 'danger.softBg' }}>
              {bulkTariffSubmitError}
            </Typography>
          ) : null}
          <Stack spacing={1.5} sx={{ mt: 2 }}>
            {bulkTariffOptions.length > 0 && (
              <FormControl>
                <FormLabel>{t('shipmentUnit.bulkTariffPickFromRules')}</FormLabel>
                <Select
                  value={null}
                  placeholder={t('shipmentUnit.bulkTariffPickFromRules')}
                  onChange={(_, v) => {
                    if (v === '__clear__') {
                      setBulkTariffCode('');
                    } else if (v) {
                      setBulkTariffCode(String(v));
                    }
                    setBulkTariffFieldError('');
                    setBulkTariffSubmitError('');
                  }}
                >
                  <Option value="__clear__">{t('shipmentUnit.bulkTariffNoneClear')}</Option>
                  {bulkTariffOptions.map((code) => (
                    <Option key={code} value={code}>{code}</Option>
                  ))}
                </Select>
              </FormControl>
            )}
            <FormControl error={!!bulkTariffFieldError}>
              <FormLabel>{t('shipmentForm.pricingTariffCode')}</FormLabel>
              <Input
                value={bulkTariffCode}
                slotProps={{ input: { maxLength: PRICING_TARIFF_CODE_MAX_LENGTH } }}
                onChange={(e) => {
                  setBulkTariffCode(e.target.value);
                  setBulkTariffFieldError('');
                  setBulkTariffSubmitError('');
                }}
                placeholder={t('shipmentUnit.bulkTariffNoneClear')}
              />
              <Typography level="body-xs" sx={{ color: 'neutral.600', mt: 0.5 }}>
                {t('shipmentUnit.bulkTariffCodeHint', { max: PRICING_TARIFF_CODE_MAX_LENGTH })}
              </Typography>
              {bulkTariffFieldError ? (
                <FormHelperText sx={{ color: 'danger.500' }}>{bulkTariffFieldError}</FormHelperText>
              ) : null}
            </FormControl>
            <Box>
              <NativeCheckbox
                checked={bulkTariffRecalculate}
                onChange={(e) => setBulkTariffRecalculate(e.target.checked)}
                label={t('shipmentUnit.bulkTariffRecalculate')}
              />
              <Typography level="body-xs" sx={{ color: 'neutral.600', mt: 0.5, pl: 3.5 }}>
                {t('shipmentUnit.bulkTariffRecalculateHint')}
              </Typography>
            </Box>
          </Stack>
          <Stack direction="row" gap={1} sx={{ mt: 2, justifyContent: 'flex-end' }}>
            <Button variant="plain" color="neutral" disabled={bulkTariffLoading} onClick={closeBulkTariffModal}>
              {t('shipmentUnit.cancel')}
            </Button>
            <Button color="warning" loading={bulkTariffLoading} onClick={handleBulkUpdateTariff}>
              {t('shipmentUnit.bulkUpdateTariff')}
            </Button>
          </Stack>
        </ModalDialog>
      </Modal>

      <Modal open={recalcConfirmOpen} onClose={closeRecalcConfirm}>
        <ModalDialog variant="outlined" role="alertdialog">
          <Typography level="title-lg">{t('shipmentUnit.recalcConfirmTitle')}</Typography>
          {recalcTargetUnit && (
            <Typography level="body-sm" sx={{ mt: 0.5, color: 'neutral.600' }}>
              {t('shipmentUnit.recalcConfirmUnit', {
                id: recalcTargetUnit.id,
                serial: recalcTargetUnit.serial_number || t('shipmentUnit.dash'),
              })}
            </Typography>
          )}
          <Typography level="body-sm" sx={{ mt: 1 }}>
            {recalcShipmentCount == null
              ? t('shipmentUnit.recalcConfirmBodyUnknown')
              : recalcShipmentCount > 10
                ? t('shipmentUnit.recalcConfirmBodyMany', { count: recalcShipmentCount })
                : t('shipmentUnit.recalcConfirmBody', { count: recalcShipmentCount })}
          </Typography>
          <Stack direction="row" gap={1} sx={{ mt: 2, justifyContent: 'flex-end' }}>
            <Button variant="plain" color="neutral" disabled={recalcLoading} onClick={closeRecalcConfirm}>
              {t('shipmentUnit.cancel')}
            </Button>
            <Button color="success" loading={recalcLoading} onClick={handleRecalculateUnitPrices}>
              {t('shipmentUnit.recalcAllPrices')}
            </Button>
          </Stack>
        </ModalDialog>
      </Modal>

      <Modal open={deleteModalOpen} onClose={() => setDeleteModalOpen(false)}>
        <ModalDialog variant="outlined" role="alertdialog">
          <Typography level="title-lg">{t('shipmentUnit.deleteModalTitle')}</Typography>
          <Typography level="body-sm" sx={{ mt: 1 }}>
            {deletingUnit && t('shipmentUnit.deleteModalBody', { id: deletingUnit.id, serial: deletingUnit.serial_number || '' })}
          </Typography>
          <Stack direction="row" gap={1} sx={{ mt: 2, justifyContent: 'flex-end' }}>
            <Button variant="plain" color="neutral" onClick={() => setDeleteModalOpen(false)}>{t('shipmentUnit.cancel')}</Button>
            <Button color="danger" loading={loading} onClick={handleDelete}>{t('shipmentUnit.delete')}</Button>
          </Stack>
        </ModalDialog>
      </Modal>
    </DirectionalMaterialThemeProvider>
  );
}
